//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Form, Input, Label } from "semantic-ui-react";
import { dailyUtcCronToLocalTime, formatUtcTimeLabel, isDailyUtcCron, localTimeToDailyUtcCron } from "./cronSchedule";

const Help = styled.div`
  color: #767676;
  font-size: 0.9em;
  margin: 0.25rem 0 0.5rem;
`;

const StoredAs = styled.div`
  color: #888;
  font-size: 0.85em;
  margin-top: 0.35rem;
  font-family: "Consolas", "Monaco", monospace;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: #4183c4;
  cursor: pointer;
  font: inherit;
  font-size: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

interface ScheduleTimeFieldProps {
  label: string;
  help: string;
  cron: string;
  disabled?: boolean;
  onCronChange: (cron: string) => void;
}

export const ScheduleTimeField = ({ label, help, cron, disabled, onCronChange }: ScheduleTimeFieldProps) => {
  const [localTime, setLocalTime] = useState("");
  const [showRaw, setShowRaw] = useState(false);
  const parsed = isDailyUtcCron(cron);

  useEffect(() => {
    if (parsed) {
      setLocalTime(dailyUtcCronToLocalTime(cron));
      setShowRaw(false);
    } else if (cron) {
      setShowRaw(true);
    }
  }, [cron, parsed]);

  const onLocalTimeChange = (value: string) => {
    setLocalTime(value);
    const nextCron = localTimeToDailyUtcCron(value);
    if (nextCron) {
      onCronChange(nextCron);
    }
  };

  return (
    <Form.Field>
      <label>{label}</label>
      <Help>{help}</Help>
      {parsed && !showRaw ? (
        <>
          <Input type="time" value={localTime} disabled={disabled} onChange={(_, data) => onLocalTimeChange(data.value)} />
          <StoredAs>
            Stored as <Label size="tiny">{cron}</Label> ({formatUtcTimeLabel(cron)}, daily)
          </StoredAs>
          {!disabled && (
            <Help>
              <TextButton type="button" onClick={() => setShowRaw(true)}>
                Edit UTC cron directly
              </TextButton>
            </Help>
          )}
        </>
      ) : (
        <>
          <Input fluid value={cron} disabled={disabled} placeholder="minute hour * * *" onChange={(_, data) => onCronChange(data.value)} />
          <Help>Expected daily UTC cron, e.g. 30 19 * * *</Help>
          {parsed && !disabled && (
            <Help>
              <TextButton type="button" onClick={() => setShowRaw(false)}>
                Use time picker
              </TextButton>
            </Help>
          )}
        </>
      )}
    </Form.Field>
  );
};
